import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { APIError } from '@vizit/auth';
import type { CustomBenchmarkScope, Retailer } from '../../generated';
import { getScorableRetailersV1WorkspacesWorkspaceIdProductCategoriesProductCategoryIdScorableRetailersGet } from '../../generated';

/*
 * GET /v1/workspaces/{workspace_id}/product-categories/{product_category_id}/scorable-retailers: the Vizit retailers
 * a collection of the category and image type can be scored against, in the picker's order. A retailer it leaves out
 * has no benchmark for the category, e.g. Walmart for an Amazon category with no Walmart twin, and a create or scoring
 * request naming it is refused. Tests answer this path with `tests/mocks/collection-scoring-handlers.ts`.
 */

export interface GetScorableRetailersQueryVariables {
  workspace_id: string;
  product_category_id: string;
  scope: CustomBenchmarkScope;
}

export type GetScorableRetailersQuery = Retailer[];

async function getScorableRetailers({ workspace_id, product_category_id, scope }: GetScorableRetailersQueryVariables) {
  const { data } =
    await getScorableRetailersV1WorkspacesWorkspaceIdProductCategoriesProductCategoryIdScorableRetailersGet({
      path: { workspace_id, product_category_id },
      query: { scope },
      throwOnError: true,
    });
  return data.retailers;
}

/** Reads a server or network failure once more; a 4xx would answer the same, and unit tests never retry. */
function shouldRetryScorableRetailers(failureCount: number, error: unknown) {
  if (import.meta.env.VITEST) {
    return false;
  }
  if (error instanceof APIError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 1;
}

export const useGetScorableRetailersQuery = <TData = GetScorableRetailersQuery, TError = unknown>(
  variables: GetScorableRetailersQueryVariables,
  options?: Omit<UseQueryOptions<GetScorableRetailersQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetScorableRetailersQuery, TError, TData>({
    queryKey: useGetScorableRetailersQuery.getKey(variables),
    queryFn: () => getScorableRetailers(variables),
    retry: shouldRetryScorableRetailers,
    ...options,
  });
};

useGetScorableRetailersQuery.getKey = ({
  workspace_id,
  product_category_id,
  scope,
}: GetScorableRetailersQueryVariables) => ['GetScorableRetailers', { workspace_id, product_category_id, scope }];

useGetScorableRetailersQuery.fetcher = (variables: GetScorableRetailersQueryVariables) => () =>
  getScorableRetailers(variables);
